<script setup lang="ts">
import { computed } from 'vue'

import { getEventTime, ParticipantLogo } from '@collector/shared-ui-domain'
import { useTeamSides } from '@desktop/views/Relation/composables'
import {
  useBetIncidents,
  useIncidentName,
} from '@desktop/views/Relation/Sports/composables'
import { injectEventClock } from '@desktop/views/Relation/Sports/provide-inject'

import { DisplayedIncident } from './types'

type Props = {
  incident: DisplayedIncident
}

const props = defineProps<Props>()

// inject
const eventClock = injectEventClock()

// composable
const { getIncidentTeamSide, getTeamSideParticipant } = useTeamSides()
const { getIncidentName } = useIncidentName()
const { isIncidentEmergencyOrBet } = useBetIncidents()

// computed
const incidentTeamSide = computed(() => getIncidentTeamSide(props.incident))

const incidentParticipant = computed(() => {
  if (incidentTeamSide.value) {
    return getTeamSideParticipant(incidentTeamSide.value)
  }
  return null
})

const incidentImportant = computed(() =>
  isIncidentEmergencyOrBet(props.incident),
)

const incidentName = computed(() => getIncidentName(props.incident.incident_id))

const incidentTime = computed(() => {
  if (!eventClock) {
    return null
  }
  return getEventTime({
    minute: props.incident.minute,
    second: props.incident.second,
  })
})
</script>

<template>
  <div
    class="text-neutral-dark-0 flex h-6 items-center rounded-sm border px-1.5 text-xs font-bold text-nowrap"
    :class="{
      'border-current-team-home-5 bg-current-team-home-9':
        incidentTeamSide === 'home',
      'border-current-team-away-5 bg-current-team-away-9':
        incidentTeamSide === 'away',
      'border-neutral-light-3 bg-neutral-light-10':
        incidentTeamSide === null && !incidentImportant,
      'border-error-5 bg-error-5/50': incidentImportant,
    }"
  >
    <div class="flex w-full items-center">
      <div class="flex w-full items-center gap-x-1">
        <ParticipantLogo
          v-if="incidentParticipant"
          :participant="incidentParticipant"
        />
        <span>
          {{ incidentTime }}
        </span>
        <span class="max-w-[95%] overflow-hidden text-nowrap text-ellipsis">
          {{ incidentName }}
        </span>
        <slot />
      </div>
    </div>
  </div>
</template>
